import { Avatar, Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import html2pdf from "html2pdf.js";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import { dashboardKeys } from "../../../../../api/queryKeys";
import LoadingIcon from "../../../../../components/LoadingIcon";
import { Toast } from "../../../../../components/Toast";
import {
  FILE_EXTENSIONS,
  generateFileName,
} from "../../../../../hooks/useFileNaming";
import { LATEST, TimePoint } from "../../../../../utils/constants";
import { icons } from "../../../../stack/environment/icons/iconExport";
import WidgetWrapper from "../../WidgetWrapper";
import {
  createPdfConfig,
  createPdfContainer,
  createPdfHeader,
  createTrendSortMenuItems,
  measureContentForPdf,
} from "../../utils/commonWidgetUtils";
import RemediationPerformanceModal from "../ToolsRemediationPerformance/RemediationPerformanceModal";
import TeamRemediationChart from "./TeamRemediationChart";

const MAX_MEMBERS_SELECTED = 8;

const TeamRemediationPerformanceWidget = forwardRef(
  ({ mainTimePoint }, ref) => {
    const { getTeamRemediationPerformanceTrend, trackEvent } = useAPI();
    const [showActivityLogTable, setShowActivityLogTable] = useState(false);
    const [baseTimePoint, setBaseTimePoint] = useState(TimePoint.LATEST);
    const colors = useColors();
    const widgetContentRef = useRef(null);

    const colorsChart = [
      colors.primary_200,
      colors.primary_100,
      colors.primary_50,
      colors.primary_25,
      colors.secondary_100,
      colors.secondary_200,
      colors.secondary_300,
      colors.secondary_400,
    ];

    const colorsChart48percent = [
      colors.primary_200_48percent,
      colors.primary_100_48percent,
      colors.primary_50_48percent,
      colors.primary_25_48percent,
      colors.secondary_100_48percent,
      colors.secondary_200_48percent,
      colors.secondary_300_48percent,
      colors.secondary_400_48percent,
    ];

    const {
      data: teamRemediationPerformanceTrend,
      isLoading: isLoadingTeamRemediationPerformanceTrend,
    } = useQuery({
      queryKey: dashboardKeys.widgetType("teamRemediationPerformanceTrend"),
      queryFn: getTeamRemediationPerformanceTrend,
      select: (data) => data.data,
    });

    const finalData = teamRemediationPerformanceTrend;

    useEffect(() => {
      if (finalData && finalData[mainTimePoint]) {
        setBaseTimePoint(mainTimePoint);
      }
    }, [mainTimePoint, finalData]);

    // Sort menu items for the dropdown filter
    const sortMenuItems = createTrendSortMenuItems(
      baseTimePoint,
      setBaseTimePoint,
      finalData,
      icons,
      trackEvent,
      "team-remediation-performance"
    );

    // Helper function to get user initials from email
    const getUserInitials = (email) => {
      if (email === "Unassigned") return "UA";
      const name = email.split("@")[0];
      const parts = name.split(/[._-]/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.slice(0, 2).toUpperCase();
    };

    // Helper function to get display name from email
    const getDisplayName = (email) => {
      if (email === "Unassigned") return "Unassigned";
      const name = email.split("@")[0];
      return name
        .split(/[._-]/)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    };

    // All team remediation performance data with current and base performance
    const allTeamRemediationData = useMemo(() => {
      if (!finalData || Object.keys(finalData).length === 0) {
        return [];
      }

      const currentData = finalData[LATEST];
      const baseData =
        baseTimePoint === LATEST ? currentData : finalData[baseTimePoint];

      return Object.entries(currentData)
        .map(([assigneeEmail, currentTeamData]) => {
          const baseTeamData = baseData[assigneeEmail] || {
            all: 0,
            HIGH: 0,
            MEDIUM: 0,
            LOW: 0,
          };

          const currentTotal = currentTeamData.all || 0;
          const baseTotal = baseTeamData.all || 0;
          const performanceChange = currentTotal - baseTotal;
          const percentageChange =
            baseTotal > 0
              ? Math.abs(Math.round((performanceChange / baseTotal) * 100))
              : 0;
          const isPositive = performanceChange >= 0;

          return {
            assigneeEmail: assigneeEmail,
            displayName: getDisplayName(assigneeEmail),
            initials: getUserInitials(assigneeEmail),
            currentTotal: currentTotal,
            baseTotal: baseTotal,
            performanceChange: performanceChange,
            percentageChange: percentageChange,
            isPositive: isPositive,
            high: currentTeamData.HIGH || 0,
            medium: currentTeamData.MEDIUM || 0,
            low: currentTeamData.LOW || 0,
          };
        })
        .filter((member) => member.currentTotal > 0) // Only show members with remediated entities
        .sort((a, b) => b.currentTotal - a.currentTotal);
    }, [finalData, baseTimePoint]);

    // Get top 8 team members by default
    const topTeamMembers = useMemo(
      () =>
        allTeamRemediationData
          .slice(0, 8)
          .map((member) => member.assigneeEmail),
      [allTeamRemediationData]
    );

    // Initialize selected team members as top 8
    const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);

    // Update selected team members when top members change (only if currently empty)
    useEffect(() => {
      if (selectedTeamMembers.length === 0 && topTeamMembers.length > 0) {
        setSelectedTeamMembers(topTeamMembers);
      }
    }, [topTeamMembers, selectedTeamMembers.length]);

    // Filter data based on selected team members
    const teamRemediationData = useMemo(
      () =>
        allTeamRemediationData.filter((member) =>
          selectedTeamMembers.includes(member.assigneeEmail)
        ),
      [allTeamRemediationData, selectedTeamMembers]
    );

    // Selected team members to display in the dropdown
    const selectedMembersToDisplay = {
      title: "Select Team Members",
      key: "selected-members",
      disabled: false,
      onclick: () => {},
      trailingIcon: icons.chevronRightIcon,
      submenuTitle: "Select Team Members (Up to 8)",
      submenuMultiple: true,
      submenu: allTeamRemediationData.map((option) => ({
        title: (
          <Box
            display={"flex"}
            flexDirection={"row"}
            alignItems={"center"}
            gap={1}
          >
            <Avatar
              sx={{
                width: 16,
                height: 16,
                fontSize: "8px",
                bgcolor: colors.primary_main,
              }}
            >
              {option.initials}
            </Avatar>
            <Typography variant="caption">{option.displayName}</Typography>
          </Box>
        ),
        key: option.assigneeEmail,
        selected: selectedTeamMembers.includes(option.assigneeEmail),
        onclick: () => {
          if (selectedTeamMembers.includes(option.assigneeEmail)) {
            setSelectedTeamMembers((prev) =>
              prev.filter((email) => email !== option.assigneeEmail)
            );
          } else {
            if (selectedTeamMembers.length < MAX_MEMBERS_SELECTED) {
              setSelectedTeamMembers((prev) => [...prev, option.assigneeEmail]);
            }
          }
        },
      })),
    };

    const handleExportToPdf = useCallback(
      async (isCombinedExport = false) => {
        if (
          !widgetContentRef.current ||
          isLoadingTeamRemediationPerformanceTrend
        )
          return null;

        if (!isCombinedExport) {
          trackEvent(
            "User clicked on Export to PDF button in Team Remediation Performance"
          );
        }

        try {
          if (isCombinedExport) {
            // For combined export, return just the widget content with header
            const widgetContainer = document.createElement("div");

            // Add widget header
            const widgetHeader = createPdfHeader(
              "Team Remediation Performance"
            );
            widgetContainer.appendChild(widgetHeader);

            // Clone and style widget content
            const widgetClone = widgetContentRef.current.cloneNode(true);
            widgetClone.style.cssText = `
          background-color: white;
          padding: 24px;
          border: none;
          width: 89%;
          min-height: fit-content;
        `;

            // Enhance chart visibility for PDF
            const chartElement =
              widgetClone.querySelector(".apexcharts-canvas");
            if (chartElement) {
              chartElement.style.cssText = `
            width: 89% !important;
            height: 350px !important;
          `;
            }

            widgetContainer.appendChild(widgetClone);

            return widgetContainer;
          } else {
            // Individual export - original functionality
            const entirePageElement = await createPdfContainer();

            const header = createPdfHeader("Team Remediation Performance");
            entirePageElement.appendChild(header);

            // Clone and style widget content
            const widgetClone = widgetContentRef.current.cloneNode(true);
            widgetClone.style.cssText = `
          background-color: white;
          padding: 24px;
          border: none;
          width: 89%;
          min-height: fit-content;
        `;

            // Enhance chart visibility for PDF
            const chartElement =
              widgetClone.querySelector(".apexcharts-canvas");
            if (chartElement) {
              chartElement.style.cssText = `
            width: 89% !important;
            height: 350px !important;
          `;
            }

            entirePageElement.appendChild(widgetClone);

            // Measure content dimensions
            const { contentWidth, contentHeight } = await measureContentForPdf(
              entirePageElement
            );

            // Generate PDF
            const pdfConfig = createPdfConfig(
              generateFileName(
                "Breeze_Team_Remediation_Performance",
                null,
                FILE_EXTENSIONS.PDF
              ),
              contentWidth,
              contentHeight
            );

            await html2pdf().set(pdfConfig).from(entirePageElement).save();
          }
        } catch (error) {
          Toast.error(
            "Failed to export Team Remediation Performance, Please try again later"
          );
          return null;
        }
      },
      [isLoadingTeamRemediationPerformanceTrend, trackEvent]
    );

    // Expose the export function to parent components
    useImperativeHandle(
      ref,
      () => ({
        exportToPdf: handleExportToPdf,
        widgetId: "teamRemediationPerformance",
        hasExportCapability: true,
      }),
      [handleExportToPdf]
    );

    return (
      <WidgetWrapper
        title={"Team Remediation Performance"}
        sortMenuItems={{
          data: [...sortMenuItems, selectedMembersToDisplay],
          tooltip: "View changes since selected time",
        }}
        info="Entities affected by remediation efforts per team member in the past 30 days"
        viewAll={{
          handleViewAll: () => {
            setShowActivityLogTable(true);
          },
          tooltip: "View all team remediation data",
          newTab: false,
          title: "Full Team Remediation list",
          hideIcon: true,
        }}
        handleExportToPdf={() => handleExportToPdf(false)}
      >
        {isLoadingTeamRemediationPerformanceTrend ? (
          <LoadingIcon />
        ) : !finalData || Object.keys(finalData).length === 0 ? (
          <Box>
            <Typography variant="caption" marginLeft={2.5}>
              No data available
            </Typography>
          </Box>
        ) : (
          <Box ref={widgetContentRef}>
            <TeamRemediationChart
              teamRemediationData={teamRemediationData}
              baseTimePoint={baseTimePoint}
              colorsChart={colorsChart}
              colorsChart48percent={colorsChart48percent}
            />
            {showActivityLogTable && (
              <RemediationPerformanceModal
                showActivityLogTable={showActivityLogTable}
                setShowActivityLogTable={setShowActivityLogTable}
              />
            )}
          </Box>
        )}
      </WidgetWrapper>
    );
  }
);

TeamRemediationPerformanceWidget.displayName =
  "TeamRemediationPerformanceWidget";

export default TeamRemediationPerformanceWidget;
